/**
 * Closed-loop learning for MetaGovernor.
 *
 * PR 3 of 8. After every repair/action cycle, observeAndLearn() decides
 * whether to persist a lesson or decision record to agentmemory. Future
 * sessions retrieve these via aggregateRead() (PR 2) and factor them into
 * scoring (PR 5).
 *
 * Design:
 * - Pure function with DI backend (no side effects without backend).
 * - config.enabled=false → returns no-op with reason.
 * - Severity threshold: minSeverityToLearn filters what gets saved.
 * - Session cap: maxLessonsPerSession prevents flooding.
 * - Lessons go to agentmemory_memory_save (type: "pattern").
 * - Decisions go to agentmemory_memory_save (type: "fact").
 * - No file I/O, no MCP calls — just decision logic + DI write.
 */

import type {
  AgentmemoryWriteBackend,
  ClosedLoopConfig,
  Decision,
  Deviation,
  LearnFromOutcomeInput,
  LearnFromOutcomeOutput,
  LessonLearned,
  MemoryDecision,
  MemoryRead,
} from "./types"

/**
 * v0.51.1 (P1 lesson-spam guard, Wave A T-5df16a0e): minimum lesson
 * confidence. Lessons below this are noise: 5311 Action-continue rows at
 * confidence 0.3 flooded recall. Only high-value lessons persist.
 */
export const MIN_LESSON_CONFIDENCE = 0.5

/**
 * Lesson confidence for a decision: the strongest available signal -
 * max(|score|, best evidence confidence) - clamped to [0.3, 0.8].
 * Evidence confidence matters: a warn at -0.4 backed by 0.8-confidence
 * evidence is high-value signal, while a neutral continue near 0 with no
 * evidence collapses to the 0.3 floor (below MIN_LESSON_CONFIDENCE).
 */
export function lessonConfidenceForDecision(decision: Decision): number {
  let evidenceMax = 0
  for (const e of decision.evidence) {
    if (typeof e.confidence === "number" && e.confidence > evidenceMax) {
      evidenceMax = e.confidence
    }
  }
  return Math.max(0.3, Math.min(0.8, Math.max(Math.abs(decision.score), evidenceMax)))
}

/**
 * Neutral continues carry no learnable signal and must never persist as
 * lessons - they were the entire 5311-row Action-continue spam class.
 */
export function isNeutralContinueDecision(decision: Decision): boolean {
  return decision.action === "continue" && Math.abs(decision.score) < MIN_LESSON_CONFIDENCE
}

/** Severity ordering for threshold comparison. */
const SEVERITY_ORDER: Record<string, number> = {
  leve: 0,
  media: 1,
  grave: 2,
}

/**
 * Generate a deterministic lesson ID from session + timestamp.
 */
function generateLessonId(sessionID: string, timestamp: string): string {
  const hash = `${sessionID}-${timestamp}`.split("").reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0)
  return `L-${Math.abs(hash).toString(36)}`
}

/**
 * Generate a deterministic decision ID from session + action + timestamp.
 */
function generateDecisionId(sessionID: string, action: string, timestamp: string): string {
  const hash = `${sessionID}-${action}-${timestamp}`.split("").reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0)
  return `D-${Math.abs(hash).toString(36)}`
}

/**
 * Check if the decision's severity meets the threshold.
 */
function severityMeetsThreshold(
  deviations: readonly Deviation[],
  threshold: ClosedLoopConfig["minSeverityToLearn"]
): boolean {
  const minOrder = SEVERITY_ORDER[threshold] ?? 0
  return deviations.some((d) => (SEVERITY_ORDER[d.severity] ?? 0) >= minOrder)
}

/**
 * Collapse repeated deviations of the same rule into ONE representative per
 * category (v0.56.0 spam-fix F2). The Files-changed-(3)(4)(5)(6) spam class
 * was N verbatim copies of the same rule concatenated with "; " — a lesson
 * must name the rule once with a ×N count and keep only the latest detail.
 */
export function collapseDeviationsForLesson(
  deviations: readonly Deviation[],
): Deviation[] {
  const byCategory = new Map<string, Deviation[]>()
  for (const d of deviations) {
    const group = byCategory.get(d.category) ?? []
    group.push(d)
    byCategory.set(d.category, group)
  }
  const collapsed: Deviation[] = []
  for (const [category, group] of byCategory) {
    if (group.length === 1) {
      collapsed.push(group[0]!)
    } else {
      // Highest severity wins; latest detail is the most current signal.
      const order = (s: string) => SEVERITY_ORDER[s] ?? 0
      const worst = [...group].sort((a, b) => order(b.severity) - order(a.severity))[0]!
      const latest = group[group.length - 1]!
      collapsed.push({
        severity: worst.severity,
        category,
        detail: `${latest.detail} (×${group.length} repeats this session)`,
        ...(latest.filePath !== undefined ? { filePath: latest.filePath } : {}),
      })
    }
  }
  return collapsed
}

/**
 * Extract concepts for FTS indexing (rule categories + file basenames).
 *
 * v0.53.1: concepts derive from REAL rule deviations, not the generic
 * ['conscience','media'] pair. v0.56.0: severity tokens stoplisted, cap 5.
 */
const CONCEPT_STOPLIST = new Set(["leve", "media", "grave", "conscience"])
export function extractConcepts(
  deviations: readonly Deviation[],
  filesChanged: readonly string[] = [],
): string[] {
  const concepts = new Set<string>()
  for (const d of deviations) {
    // v0.56.0: severity tokens ("media"/"leve"/"grave") and the synthetic
    // "conscience" wrapper are stoplisted — they match everything in FTS
    // recall and re-created the spam class. Only REAL rule categories index.
    if (!CONCEPT_STOPLIST.has(d.category)) concepts.add(d.category)
    if (d.filePath) {
      // Index the basename for FTS lookup by tool/file name
      const basename = basenameOf(d.filePath)
      if (basename && !CONCEPT_STOPLIST.has(basename)) concepts.add(basename)
    }
  }
  // v0.17.2: also index file basenames from broader file change set
  for (const filePath of filesChanged) {
    const basename = basenameOf(filePath)
    if (basename && !CONCEPT_STOPLIST.has(basename)) concepts.add(basename)
  }
  // v0.56.0: cap at 5 — unbounded concept lists dilute FTS precision.
  return [...concepts].slice(0, 5)
}

/**
 * Build the lesson content string from a decision and its deviations.
 * v0.56.0: deviations collapse by rule first — repeated identical violations
 * persist as ONE line with a ×N count, never N verbatim dumps.
 */
function buildLessonContent(decision: Decision, deviations: readonly Deviation[]): string {
  const collapsed = collapseDeviationsForLesson(deviations)
  const deviationSummary = collapsed
    .map((d) => `[${d.severity}] ${d.category}: ${d.detail}`)
    .join("; ")
  return `Action "${decision.action}" (score ${decision.score.toFixed(2)}) after deviations: ${deviationSummary}. Reasoning: ${decision.reasoning}`
}

/**
 * Generic fallback whatToDo used only when no escalation target and no known
 * rule guides the lesson. v0.56.0 (F3 value gate): a lesson carrying ONLY this
 * fallback teaches nothing and must not persist unless grounded. Single
 * canonical constant so the gate and the producer can never drift apart.
 */
export const GENERIC_WHAT_TO_DO = "Re-read the plan and continue with verification"

/**
 * Per-rule corrective guidance (v0.56.0 F5 judge-synthesis). Each protocol
 * rule maps to the SPECIFIC corrective action — never the generic "re-read
 * the plan". The plugin orients with the violated rule's own remedy, grounded
 * in the deviation evidence, instead of emitting template commands.
 */
const RULE_GUIDANCE: Record<string, string> = {
  "no-type-suppression":
    "Remove the `as any`/`@ts-ignore` suppression and fix the underlying type with proper narrowing",
  "no-empty-catch":
    "Log the error or handle it gracefully — an empty catch hides failures",
  "memory-first":
    "Query memory (`omo_recall`, then `omo_recall_mcp`) before asking the user — the answer may already be known",
  "empty-result-escalation":
    "On empty recall, escalate to `omo_recall_mcp` before asking the user",
  "codebase-graph-first":
    "Query the code graph (`omo_search`) before grep/glob for architecture or symbol questions",
  "continuation-discipline":
    'Resume via `task(task_id="ses_...")` instead of starting a fresh task — preserves context and saves tokens',
  "save-discovery-to-memory":
    "Persist the non-obvious finding via `omo_remember` so future sessions benefit",
  "no-batch-todo-completion":
    "Mark each todo `completed` immediately after finishing it — do not batch-complete",
  "oracle-verification":
    "Request Oracle review before proceeding",
}

/**
 * Synthesize an evidence-grounded whatToDo (v0.56.0 F5). Escalation targets
 * keep their specific directives; otherwise the worst-severity deviation's
 * rule guidance wins. GENERIC_WHAT_TO_DO is the last resort (unknown rule),
 * and the value gate will reject it unless the lesson is grounded.
 */
export function synthesizeWhatToDo(
  deviations: readonly Deviation[],
  escalateTo: "oracle" | "user" | null,
): string {
  if (escalateTo === "oracle") return "Request Oracle review before proceeding"
  if (escalateTo === "user") return "Ask the user for guidance before proceeding"
  const order = (s: string) => SEVERITY_ORDER[s] ?? 0
  const worst = [...deviations].sort((a, b) => order(b.severity) - order(a.severity))[0]
  if (worst && RULE_GUIDANCE[worst.category] !== undefined) return RULE_GUIDANCE[worst.category]!
  return GENERIC_WHAT_TO_DO
}

/**
 * Value gate for conscience lessons (v0.56.0 F3). Returns false when the
 * lesson is not actionable: a mistake body too short to carry signal, or the
 * generic fallback whatToDo on an ungrounded lesson (no file/place scope and
 * no grave deviation — "re-read the plan" applies to every stop ever and
 * teaches nothing). A generic whatToDo on a GROUNDED lesson (real files) or
 * a grave deviation still persists: the mistake names a real rule + place.
 */
export function isActionableConscienceLesson(
  mistake: string,
  whatToDo: string,
  deviations: readonly Deviation[],
  files: readonly string[] = [],
): boolean {
  if (mistake.trim().length < 20) return false
  if (whatToDo === GENERIC_WHAT_TO_DO) {
    if (deviations.some((d) => d.severity === "grave")) return true
    return files.some((f) => f.trim().length > 0)
  }
  return true
}

/**
 * Novelty check against already-recalled lessons (v0.56.0 F3). Token-overlap
 * (Jaccard) between the candidate mistake and each recalled lesson's
 * title+concepts; overlap >= threshold means "already known — don't persist".
 * Pure function; the caller owns recall (observeAndLearn threads memoryRead,
 * the auto-remember path threads its own recall when wired).
 */
export function isNovelAgainstRecall(
  mistake: string,
  recalled: readonly { title?: string; concepts?: readonly string[] }[],
  threshold = 0.7,
): boolean {
  const tokensOf = (s: string) =>
    new Set(s.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length > 2))
  const candidate = tokensOf(mistake)
  if (candidate.size === 0) return false
  for (const lesson of recalled) {
    const other = tokensOf(`${lesson.title ?? ""} ${(lesson.concepts ?? []).join(" ")}`)
    if (other.size === 0) continue
    let intersection = 0
    for (const t of candidate) if (other.has(t)) intersection++
    const jaccard = intersection / (candidate.size + other.size - intersection)
    if (jaccard >= threshold) return false
  }
  return true
}

/**
 * Core learning function. Decides whether to save a lesson and/or decision
 * to agentmemory based on the outcome of a repair/action cycle.
 *
 * Returns LearnFromOutcomeOutput describing what was saved (or why nothing was saved).
 */
export async function observeAndLearn(
  input: LearnFromOutcomeInput,
  backend: AgentmemoryWriteBackend
): Promise<LearnFromOutcomeOutput> {
  const { decision, config, sessionID, directory, filesChanged } = input
  const now = new Date().toISOString()

  // Config disabled → no-op
  if (!config.enabled) {
    return { lessonSaved: null, decisionSaved: null, reason: "closed-loop learning disabled" }
  }

  // No deviations → nothing to learn from
  if (decision.evidence.length === 0 && decision.action === "continue") {
    return { lessonSaved: null, decisionSaved: null, reason: "no deviations to learn from" }
  }

  let lessonSaved: LessonLearned | null = null
  let decisionSaved: MemoryDecision | null = null

  // Save decision record if enabled
  if (config.saveDecisions) {
    const decisionRecord: MemoryDecision = {
      id: generateDecisionId(sessionID, decision.action, now),
      timestampISO: now,
      action: decision.action,
      score: decision.score,
      reasoning: decision.reasoning,
      sessionID,
      directory,
      deviations: decision.evidence
        .filter((e) => e.source === "deviation-detector")
        .map((e) => ({
          severity: "media" as const,
          category: e.source,
          detail: e.value,
        })),
    }

    try {
      await backend.saveMemory({
        content: `Decision: ${decision.action} (score ${decision.score.toFixed(2)}). ${decision.reasoning}`,
        concepts: ["meta-governor", "decision", decision.action],
        type: "fact",
        files: [...filesChanged],
      })
      decisionSaved = decisionRecord
    } catch {
      // Backend failure is non-fatal — degrade silently
    }
  }

  // Save lesson if deviations meet severity threshold
  const deviationsFromEvidence = decision.evidence
    .filter((e) => e.source === "deviation-detector")
    .map<Deviation>((e) => ({
      severity: "media",
      category: e.source,
      detail: e.value,
    }))

  // v0.17.0 (F5.4): enforce maxLessonsPerSession cap.
  // Cap is inclusive — when currentLessonCount >= cap, skip the lesson save.
  const currentLessonCount = input.currentLessonCount ?? 0
  if (currentLessonCount >= config.maxLessonsPerSession) {
    return {
      lessonSaved: null,
      decisionSaved,
      reason: `maxLessonsPerSession cap reached (${currentLessonCount} >= ${config.maxLessonsPerSession})`,
    }
  }

  // v0.17.2 (Gap D): when saveLessons is explicitly false, skip lesson save.
  // Default is true (lesson saves unless explicitly disabled).
  const saveLessonsEnabled = config.saveLessons !== false
  // v0.51.1 (P1 spam guard): WHY a lesson was skipped. Surfaced in reason.
  let lessonSkipReason: string | null = null
  if (
    saveLessonsEnabled &&
    severityMeetsThreshold(deviationsFromEvidence, config.minSeverityToLearn)
  ) {
    if (isNeutralContinueDecision(decision)) {
      // Neutral continues carry no learnable signal - persisting them
      // produced the 5311-row Action-continue noise class (Wave A P1).
      lessonSkipReason = "neutral continue carries no learnable signal (action=continue score=" + decision.score.toFixed(2) + ")"
    } else {
      const lessonConfidence = lessonConfidenceForDecision(decision)
      if (lessonConfidence < MIN_LESSON_CONFIDENCE) {
        lessonSkipReason = "confidence below threshold (" + lessonConfidence.toFixed(2) + " < " + MIN_LESSON_CONFIDENCE.toFixed(2) + ")"
      } else if (
        // v0.56.0 (F3 novelty): the candidate lesson restates an already-recalled
        // lesson (Jaccard overlap on title+concepts) — persisting it teaches nothing.
        !isNovelAgainstRecall(
          buildLessonContent(decision, deviationsFromEvidence),
          input.memoryRead?.agentmemory?.lessons ?? [],
        )
      ) {
        lessonSkipReason = "not novel vs recalled lessons (already known)"
      } else {
        const concepts = extractConcepts(deviationsFromEvidence, filesChanged)
        const content = buildLessonContent(decision, deviationsFromEvidence)
        // v0.51.1: stable dedupe key - backends dedupe on this (real dedupe
        // in save, not just the autoRemember cooldown). Score floats are
        // excluded so jitter cannot defeat it.
        const dedupeKey = conscienceDedupeKey({
          action: decision.action,
          evidenceSources: decision.evidence.map((e) => e.source),
          deviationCategories: deviationsFromEvidence.map((d) => d.category),
        })

        try {
          const result = await backend.saveLesson({
            content,
            context: `session:${sessionID} dir:${directory}`,
            confidence: lessonConfidence,
            tags: concepts,
            dedupeKey,
          })

          if (result.deduped === true) {
            lessonSkipReason = "duplicate suppressed (dedupeKey=" + dedupeKey + ")"
          } else {
            lessonSaved = {
              id: result.id,
              title: `${decision.action} after ${deviationsFromEvidence[0]?.category ?? "deviation"}`,
              content,
              type: "pattern",
              concepts,
              confidence: lessonConfidence,
              files: [...filesChanged],
              sessionID,
            }
          }
        } catch {
          // Backend failure is non-fatal — degrade silently
        }
      }
    }
  }

  // Determine reason
  const reasons: string[] = []
  if (decisionSaved) reasons.push("decision saved")
  if (lessonSaved) {
    reasons.push("lesson saved")
  } else if (lessonSkipReason) {
    reasons.push(lessonSkipReason)
  } else if (!saveLessonsEnabled) {
    reasons.push("saveLessons disabled")
  } else if (!severityMeetsThreshold(deviationsFromEvidence, config.minSeverityToLearn)) {
    reasons.push("severity below threshold")
  } else {
    reasons.push("no saveable content")
  }

  return {
    lessonSaved,
    decisionSaved,
    reason: reasons.join("; ") || "no action taken",
  }
}

/**
 * Helper: create a default ClosedLoopConfig.
 */
export function defaultClosedLoopConfig(): ClosedLoopConfig {
  return {
    enabled: true,
    minSeverityToLearn: "media",
    maxLessonsPerSession: 20,
    saveDecisions: true,
    // v0.17.2: saveLessons default true. Set to false to disable lesson writes
    // while keeping decision records.
    saveLessons: true,
    // Conscience fix (D2): auto-remember is opt-in — default disabled to end
    // AgentMemory garbage. Keep 5min cooldown + dedupe defaults.
    autoRemember: { enabled: false, cooldownMs: 300_000, dedupe: true },
  }
}

/**
 * Conscience value-gate (D3). Single gate for all conscience memory writes.
 *
 * Requires ALL of:
 * - action in {escalate, stop} (D1 — warn/continue never persist)
 * - config.enabled
 * - config.saveLessons !== false
 * - severityMeetsThreshold(deviations, config.minSeverityToLearn)
 * - novelty === true (caller-owned novelty check)
 * - lessonCount < config.maxLessonsPerSession
 */
export interface ShouldPersistConscienceMemoryInput {
  readonly action: Decision["action"]
  readonly deviations: readonly Deviation[]
  readonly config: ClosedLoopConfig
  readonly novelty: boolean
  readonly lessonCount: number
}

export function shouldPersistConscienceMemory(input: ShouldPersistConscienceMemoryInput): boolean {
  const { action, deviations, config, novelty, lessonCount } = input
  if (action !== "escalate" && action !== "stop") return false
  if (!config.enabled) return false
  if (config.saveLessons === false) return false
  if (!severityMeetsThreshold(deviations, config.minSeverityToLearn)) return false
  if (novelty !== true) return false
  if (lessonCount >= config.maxLessonsPerSession) return false
  return true
}

/**
 * Stable dedupe key (D5). Hash of stable fields only — action + sorted UNIQUE
 * evidence sources + sorted UNIQUE deviation categories. Score floats are
 * deliberately excluded: float jitter previously defeated dedupe.
 * v0.56.0: multiplicity-insensitive — ["oracle-verification" ×4] and
 * ["oracle-verification" ×1] hash identically (uniq before join), otherwise
 * the accumulating deviation list defeats dedupe exactly when it matters most.
 */
export interface ConscienceDedupeKeyInput {
  readonly action: string
  readonly evidenceSources: readonly string[]
  readonly deviationCategories: readonly string[]
}

export function conscienceDedupeKey(input: ConscienceDedupeKeyInput): string {
  const sources = [...new Set(input.evidenceSources)].sort().join(",")
  const categories = [...new Set(input.deviationCategories)].sort().join(",")
  const stable = `${input.action}|${sources}|${categories}`
  const hash = stable.split("").reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0)
  return `C-${Math.abs(hash).toString(36)}`
}

/**
 * Basename for FTS indexing — handles POSIX and Windows separators.
 * Keeps whereToGo/concepts as real PLACES (file names) instead of
 * meta-instructions about which tool to call.
 */
export function basenameOf(filePath: string): string {
  const parts = filePath.split(/[\\/]/);
  const base = parts.pop() ?? filePath;
  return base.trim();
}

/**
 * Derive the conscience `mistake` from REAL rule deviations — never the raw
 * scoring reasoning. A raw dump like 'Stop (score: -0.552): primary concern:
 * Iteration ratio...' describes the JUDGE ('the session is long'), not a
 * learnable rule violation. The learnable unit is `${category}: ${detail}`
 * from the first non-conscience deviation; when several exist they are joined
 * with '; ' and truncated to ~300 chars so the saved lesson stays scannable.
 */
export function conscienceMistakeFromDeviations(deviations: readonly Deviation[]): string {
  const real = deviations.filter((d) => d.category !== "conscience");
  const source = real.length > 0 ? real : deviations;
  const joined = source.map((d) => `${d.category}: ${d.detail}`).join("; ");
  return joined.slice(0, 300);
}

/**
 * Derive conscience `whereToGo` as a PLACE — basenames of changed files —
 * never meta-instructions. The old value ('Persist via omo_remember; recall
 * via omo_recall...') told the agent WHERE in tool-space, not where in the
 * repo the rule applies. When no files exist, `session:<id>` scopes the
 * lesson to the session instead of inventing a location.
 */
export function conscienceWhereToGo(files: readonly string[], sessionID: string): string {
  const bases = files.map((f) => basenameOf(f)).filter((b) => b.length > 0);
  const unique = [...new Set(bases)].slice(0, 3);
  if (unique.length > 0) return unique.join(", ");
  return `session:${sessionID}`;
}

/**
 * Structured conscience memory content (D4/D6). Built via buildLessonContent
 * + extractConcepts — never a raw dump. Delivery route is always the
 * Zod-validated omo_remember tool.
 *
 * v0.53.1 (conscience-content fix): content is pure lesson — no `ToolRoute:`
 * self-reference. The agent already knows the tool from the preamble
 * ('Conscience lesson — persist it via omo_remember'); persisting the route
 * inside the lesson pollutes recall with meta-instructions. `toolRoute` is
 * kept as an optional deprecated passthrough so old callers still compile,
 * but it is NEVER rendered. Concepts derive from REAL deviations via
 * extractConcepts, not the generic ['conscience','media'] pair.
 */
export interface BuildConscienceMemoryContentInput {
  // Oracle note 3: real decision action - stop lessons must read Action stop, not escalate.
  readonly action: "escalate" | "stop"
  readonly mistake: string
  readonly whatToDo: string
  readonly whereToGo: string
  /**
   * Deprecated self-reference — accepted for compat but never rendered.
   * The preamble already names omo_remember; the saved CONTENT must be
   * pure lesson (mistake/whatToDo/whereToGo) without tool routing noise.
   */
  readonly toolRoute?: "omo_remember"
  readonly score: number
  readonly files: readonly string[]
  /**
   * Real rule deviations (category !== 'conscience'). Used for the lesson
   * base + concepts so recall indexes violated rules, not the synthetic
   * conscience wrapper. When omitted, falls back to the legacy synthetic
   * single-deviation for backward compat with direct unit callers.
   */
  readonly deviations?: readonly Deviation[]
}

export interface ConscienceMemoryContent {
  readonly content: string
  readonly concepts: string[]
}

export function buildConscienceMemoryContent(input: BuildConscienceMemoryContentInput): ConscienceMemoryContent {
  const provided = (input.deviations ?? []).filter((d) => d.category !== "conscience");
  const deviations: readonly Deviation[] = provided.length > 0 ? provided : [
    { severity: "media", category: "conscience", detail: input.mistake },
  ]
  const decision: Decision = {
    action: input.action,
    score: input.score,
    reasoning: `${input.whatToDo} -> ${input.whereToGo}`,
    evidence: [],
    shouldEscalateTo: null,
  }
  const base = buildLessonContent(decision, deviations)
  const content = `${base}\nMistake: ${input.mistake}\nWhatToDo: ${input.whatToDo}\nWhereToGo: ${input.whereToGo}`
  const concepts = extractConcepts(deviations, input.files)
  return { content, concepts }
}

export { SEVERITY_ORDER }
